import { Check, Send } from "lucide-react";
import { useEffect, useId, useRef } from "react";

import {
  BUTTON_CONFIRM,
  BUTTON_SECONDARY,
  BUTTON_SEND,
  FIELD_INPUT,
  STEP_HEADING,
} from "./ui";
import type { SendFlow } from "./useSendFlow";

/** The same check the backend makes on a test address. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The backend takes at most this many test addresses at once. */
const MAX_TEST_ADDRESSES = 5;

interface TestAddresses {
  addresses: string[];
  /** Why they can't be used yet, in words for the sender. */
  problem: string | null;
}

function readTestAddresses(text: string, multiple: boolean): TestAddresses {
  const addresses = multiple
    ? text
        .split(/[,;\s]+/)
        .map((address) => address.trim())
        .filter(Boolean)
    : [text.trim()].filter(Boolean);
  if (addresses.length > MAX_TEST_ADDRESSES) {
    return {
      addresses,
      problem: `Up to ${MAX_TEST_ADDRESSES} test addresses at a time.`,
    };
  }
  const wrong = addresses.find((address) => !EMAIL_PATTERN.test(address));
  return {
    addresses,
    problem: wrong ? `That email address doesn't look right: ${wrong}` : null,
  };
}

/**
 * The two steps every email on the page takes: send a test and look at
 * it, then send to everyone behind a confirm that says exactly what will
 * happen. The panel owns its request; this owns the rules (a fresh test
 * before every send, a moment before "Yes, send it" answers).
 */
export function TestThenSend({
  flow,
  emailName,
  audience,
  noAudienceText,
  multipleAddresses = false,
  busy,
  onSendTest,
  onSendAll,
  alreadySentNote = null,
}: {
  flow: SendFlow;
  /** The email's name as the page shows it, quoted in the confirm. */
  emailName: string;
  /** Who a send to everyone reaches ("14 people"); null when no one. */
  audience: string | null;
  /** Shown in place of the send button while audience is null. */
  noAudienceText: string;
  /** The box takes up to 5 addresses separated by commas. */
  multipleAddresses?: boolean;
  busy: boolean;
  onSendTest: (addresses: string[]) => void;
  onSendAll: () => void;
  /** "This email already went to 12 people on 12 Sep, 7:40 pm." */
  alreadySentNote?: string | null;
}) {
  const inputId = useId();
  const helperId = useId();
  const confirmId = useId();
  const goBackRef = useRef<HTMLButtonElement>(null);
  const { confirm } = flow;
  const { addresses, problem } = readTestAddresses(
    flow.testAddress,
    multipleAddresses,
  );

  // Put the sender on the confirm box, starting from the safe answer.
  useEffect(() => {
    if (confirm.armed) {
      goBackRef.current?.focus();
    }
  }, [confirm.armed]);

  const sendTest = () => {
    if (problem) {
      flow.problem(problem);
      return;
    }
    flow.testStarted();
    onSendTest(addresses);
  };

  const sendAll = () => {
    if (!confirm.ready || busy) {
      return;
    }
    flow.sendAllStarted();
    onSendAll();
  };

  const testLabel =
    multipleAddresses && addresses.length > 1
      ? `Send a test to ${addresses.length} people`
      : "Send me a test";

  return (
    <div className="mt-5 space-y-6 border-t border-[color:var(--color-line)] pt-5">
      <div>
        <p className={STEP_HEADING}>Check it first</p>
        <label
          htmlFor={inputId}
          className="mt-3 block text-[14px] font-semibold text-[color:var(--color-ink)]"
        >
          {multipleAddresses
            ? "Send a test to (up to 5 addresses, separated by commas)"
            : "Send a test to"}
        </label>
        <div className="mt-1.5 flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            id={inputId}
            type={multipleAddresses ? "text" : "email"}
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            value={flow.testAddress}
            onChange={(e) => flow.editTestAddress(e.target.value)}
            className={FIELD_INPUT}
          />
          <button
            type="button"
            onClick={sendTest}
            disabled={busy || addresses.length === 0}
            className={BUTTON_SECONDARY}
          >
            {busy && !confirm.armed ? "Sending…" : testLabel}
          </button>
        </div>
      </div>

      <div>
        <p className={STEP_HEADING}>Send to everyone</p>
        {audience === null ? (
          <p className="mt-2 text-[15px] text-[color:var(--color-ink-soft)]">
            {noAudienceText}
          </p>
        ) : confirm.armed ? (
          <div
            role="group"
            aria-labelledby={confirmId}
            className="mt-3 rounded-lg border border-[color:var(--color-amber)] bg-[color:var(--color-amber)]/8 p-4"
          >
            <p
              id={confirmId}
              className="text-[16px] font-semibold text-[color:var(--color-ink)]"
            >
              Send '{emailName}' to {audience} now? You can't undo this.
            </p>
            {alreadySentNote ? (
              <p className="mt-1.5 text-[15px] text-[color:var(--color-ink-soft)]">
                {alreadySentNote}
              </p>
            ) : null}
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={sendAll}
                disabled={busy || !confirm.ready}
                className={BUTTON_CONFIRM}
              >
                <Check size={16} aria-hidden />
                {busy ? "Sending…" : "Yes, send it"}
              </button>
              <button
                ref={goBackRef}
                type="button"
                onClick={confirm.disarm}
                disabled={busy}
                className={`${BUTTON_SECONDARY} sm:w-full`}
              >
                No, go back
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-3">
            <button
              type="button"
              onClick={confirm.arm}
              disabled={busy || !flow.testedOk}
              aria-describedby={flow.testedOk ? undefined : helperId}
              className={BUTTON_SEND}
            >
              <Send size={15} aria-hidden />
              Send to {audience}
            </button>
            {flow.testedOk ? null : (
              <p
                id={helperId}
                className="mt-2 text-[14px] text-[color:var(--color-ink-soft)]"
              >
                Send yourself a test first.
              </p>
            )}
          </div>
        )}
      </div>

      <div aria-live="polite">
        {flow.notice ? (
          <p
            className={`text-[15px] leading-relaxed ${
              flow.notice.tone === "problem"
                ? "text-[color:var(--color-danger)]"
                : "text-[color:var(--color-ink)]"
            }`}
          >
            {flow.notice.text}
          </p>
        ) : null}
      </div>
    </div>
  );
}
